import { useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { formatDistanceToNow } from "date-fns";
import {
  Badge,
  Checkbox,
  DataTable,
  Field,
  localTablePage,
  Mono,
  Progress,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  StatusBadge,
  TableIdentity,
  updateTableState,
  type DataTableColumn,
  type DataTableState,
} from "@h2j-data-team/ui";
import type { ReloadAppStats } from "@repo/shared";

import { DensityToggle } from "@/components/density-toggle";
import { supportCode } from "@/components/error-screen";
import { reloadAppsQueryOptions } from "@/features/reloads/api";
import { useFormatDate } from "@/hooks/use-format-date";
import { useLocale } from "@/hooks/use-locale";
import type { ReloadPeriodParams } from "@/lib/api/reloads";
import { formatSeconds } from "@/lib/duration-format";
import { listRefreshing, paginationLabels, tableMessages, tableSearch } from "@/lib/ui-labels";
import { useSearchHotkey } from "@/lib/use-search-hotkey";
import { useTableDensity } from "@/lib/use-table-density";
import { appLabel, reloadStatusLabel, reloadStatusTone } from "./labels";
import { ViewRunsButton } from "./AppRunsDrawer";
import { QlikAppLink } from "./QlikAppLink";

const PAGE_SIZE = 50;
// The trend (%) from which a change is highlighted, slower or faster.
const TREND_PCT = 20;
// The Select's "no filter" item: Radix rejects an empty item value, so the
// state keeps `undefined` and only the control sees this.
const ALL_VALUE = "__all__";

// The "Last" filter's options, named by the status that labels them, with the
// raw Qlik statuses each one covers.
const LAST_GROUPS: Record<string, string[]> = {
  SUCCEEDED: ["SUCCEEDED"],
  FAILED: ["FAILED", "EXCEEDED_LIMIT"],
  RELOADING: ["QUEUED", "RELOADING", "CANCELING"],
};

type AppFilters = { last?: string; slower?: true };

// Waiting in the queue for more than this share of the app's median run.
const HEAVY_WAIT_RATIO = 0.2;

const isWaitHeavy = (app: ReloadAppStats) =>
  app.medianWaitSeconds !== null &&
  app.medianSeconds !== null &&
  app.medianWaitSeconds > HEAVY_WAIT_RATIO * app.medianSeconds;

export function AppsView({
  connectionId,
  baseUrl,
  period,
  version,
  totalSeconds,
  onViewRuns,
}: {
  connectionId: string;
  baseUrl: string;
  period: ReloadPeriodParams;
  version: string;
  // every app's hours in the period, for each one's share; undefined while loading
  totalSeconds: number | undefined;
  onViewRuns: (appId: string) => void;
}) {
  const { t } = useTranslation("reloads");
  const { t: tc } = useTranslation("common");
  const { language, dateFnsLocale } = useLocale();
  const { formatDateTime } = useFormatDate();
  const { density, setDensity } = useTableDensity();
  const searchRef = useRef<HTMLInputElement>(null);
  useSearchHotkey(searchRef);

  const query = useQuery(reloadAppsQueryOptions(connectionId, period, version));
  const apps = query.data ?? [];

  const [state, setState] = useState<DataTableState<AppFilters>>({
    page: 1,
    pageSize: PAGE_SIZE,
    query: "",
    filters: {},
    selection: [],
  });

  // The app that takes the most hours, whatever the table is sorted by.
  const heaviest = apps.reduce<ReloadAppStats | undefined>(
    (top, app) => (!top || app.totalSeconds > top.totalSeconds ? app : top),
    undefined,
  )?.appId;

  const count = (value: number) => value.toLocaleString(language);
  const percent = (ratio: number) =>
    ratio.toLocaleString(language, { style: "percent", maximumFractionDigits: 1 });
  const oneDecimal = (value: number) =>
    value.toLocaleString(language, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  const duration = (seconds: number | null) => (seconds === null ? "—" : formatSeconds(seconds));
  const signedPercent = (pct: number) => {
    const rounded = Math.round(pct * 10) / 10;
    const sign = rounded > 0 ? "+" : rounded < 0 ? "−" : "";
    return `${sign}${Math.abs(rounded).toLocaleString(language, { maximumFractionDigits: 1 })}%`;
  };

  const columns: DataTableColumn<ReloadAppStats>[] = [
    {
      id: "app",
      header: t("apps.columns.app"),
      variant: "identity",
      rowHeader: true,
      sort: "text",
      value: appLabel,
      cell: (app) => (
        <TableIdentity
          name={<QlikAppLink app={app} baseUrl={baseUrl} />}
          detail={
            app.appId === heaviest &&
            apps.length > 1 && <Badge variant="secondary">{t("apps.heaviest")}</Badge>
          }
        />
      ),
    },
    {
      id: "perDay",
      header: t("apps.columns.perDay"),
      width: "6.5rem",
      align: "end",
      variant: "numeric",
      sort: "number",
      value: (app) => app.perDay,
      cell: (app) => oneDecimal(app.perDay),
    },
    {
      id: "wait",
      header: t("apps.columns.wait"),
      width: "5.5rem",
      align: "end",
      variant: "numeric",
      sort: "number",
      value: (app) => app.medianWaitSeconds,
      cell: (app) =>
        isWaitHeavy(app) ? (
          <span
            className="font-semibold text-status-warning-foreground"
            title={t("apps.waitHeavy")}
          >
            {duration(app.medianWaitSeconds)}
          </span>
        ) : (
          duration(app.medianWaitSeconds)
        ),
    },
    {
      id: "median",
      header: t("apps.columns.median"),
      width: "7rem",
      align: "end",
      variant: "numeric",
      sort: "number",
      value: (app) => app.medianSeconds,
      cell: (app) => duration(app.medianSeconds),
    },
    {
      id: "p90",
      header: t("apps.columns.p90"),
      width: "5.5rem",
      align: "end",
      variant: "numeric",
      sort: "number",
      value: (app) => app.p90Seconds,
      cell: (app) => duration(app.p90Seconds),
    },
    {
      id: "trend",
      header: t("apps.columns.trend"),
      width: "7.5rem",
      align: "end",
      variant: "numeric",
      sort: "number",
      value: (app) => app.trendPct,
      cell: ({ trendPct: pct }) => {
        if (pct === null) return "—";
        if (pct >= TREND_PCT)
          return (
            <span className="font-semibold text-status-warning-foreground">
              <span aria-hidden>▲ </span>
              {signedPercent(pct)}
            </span>
          );
        if (pct <= -TREND_PCT)
          return (
            <span className="text-status-success-foreground">
              <span aria-hidden>▼ </span>
              {signedPercent(pct)}
            </span>
          );
        return signedPercent(pct);
      },
    },
    {
      id: "share",
      header: t("apps.columns.share"),
      width: "8.5rem",
      sort: "number",
      value: (app) => app.totalSeconds,
      // The app's hours are in the tooltip.
      cell: (app) => {
        const hours = t("apps.hours", { value: oneDecimal(app.totalSeconds / 3600) });
        if (!totalSeconds) return <span title={hours}>—</span>;
        return (
          <span className="flex items-center gap-2" title={hours}>
            <Progress
              value={app.totalSeconds}
              max={totalSeconds}
              size="md"
              className="w-12"
              aria-hidden
            />
            <Mono>{percent(app.totalSeconds / totalSeconds)}</Mono>
          </span>
        );
      },
    },
    {
      id: "failed",
      header: t("apps.columns.failures"),
      width: "6.5rem",
      align: "end",
      variant: "numeric",
      sort: "number",
      value: (app) => app.failed,
      cell: (app) => count(app.failed),
    },
    {
      id: "last",
      header: t("apps.columns.last"),
      // The badge alone keeps the app name readable at 1440px; when it ran is
      // in the tooltip.
      width: "8.5rem",
      cell: (app) => (
        <StatusBadge
          tone={reloadStatusTone(app.lastStatus)}
          title={`${formatDistanceToNow(new Date(app.lastReloadAt), {
            addSuffix: true,
            locale: dateFnsLocale,
          })} · ${formatDateTime(app.lastReloadAt)}`}
        >
          {reloadStatusLabel(t, app.lastStatus)}
        </StatusBadge>
      ),
    },
  ];

  const page = localTablePage(apps, state, {
    columns,
    search: appLabel,
    filter: (app, { last, slower }) =>
      (!last || LAST_GROUPS[last].includes(app.lastStatus)) &&
      (!slower || (app.trendPct !== null && app.trendPct >= TREND_PCT)),
  });

  const setFilters = (patch: Partial<AppFilters>) =>
    setState((prev) => updateTableState(prev, { filters: { ...prev.filters, ...patch } }));

  const supportInfo = supportCode(query.error, tc("error.supportCode"));

  return (
    <div className="flex flex-col gap-3">
      <DataTable
        label={t("apps.title")}
        density={density}
        columns={columns}
        rows={page.rows}
        getRowId={(app) => app.appId}
        state={state}
        onStateChange={setState}
        loading={query.isPending}
        refreshing={listRefreshing(query)}
        error={
          query.isError
            ? {
                title: t("error.load"),
                description: supportInfo && (
                  <>
                    {supportInfo.label}: <Mono>{supportInfo.value}</Mono>
                  </>
                ),
              }
            : null
        }
        onRetry={() => void query.refetch()}
        search={{
          ...tableSearch(t("apps.searchPlaceholder")),
          inputRef: searchRef,
          shortcutHint: "/",
        }}
        toolbar={
          <>
            <Select
              value={state.filters.last ?? ALL_VALUE}
              onValueChange={(value) =>
                setFilters({ last: value === ALL_VALUE ? undefined : value })
              }
            >
              <SelectTrigger size="compact" className="w-44" aria-label={t("apps.lastLabel")}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_VALUE}>{t("apps.lastAll")}</SelectItem>
                {Object.keys(LAST_GROUPS).map((status) => (
                  <SelectItem key={status} value={status}>
                    {t(`status.${status}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Field inline label={t("apps.onlySlower")}>
              <Checkbox
                size="compact"
                checked={state.filters.slower === true}
                onCheckedChange={(checked) =>
                  setFilters({ slower: checked === true ? true : undefined })
                }
              />
            </Field>
            <DensityToggle value={density} onChange={setDensity} />
          </>
        }
        rowActions={{
          label: t("actions.column"),
          width: 56,
          render: (app) => <ViewRunsButton app={app} onClick={() => onViewRuns(app.appId)} />,
        }}
        pagination={{ kind: "offset", total: page.total, labels: paginationLabels() }}
        messages={tableMessages({ empty: { title: t("apps.empty") } })}
      />
      {apps.length > 0 && (
        <p className="text-xs text-faint">
          {t("apps.summary", { count: apps.length, formatted: count(apps.length) })}{" "}
          {t("apps.method")}
        </p>
      )}
    </div>
  );
}
